import { lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';

import styles from './app/[brand]/page.module.css';
import { Header, Home, Init, NavBar, RestrictedPage } from './containers';
import './globals.css';
import useBrand from './hooks/useBrand';
import { Brand } from './types/Brand';

const AdministratorsPage = lazy(
  () => import('./app/[brand]/administrators/page'),
);
const AdministratorsCreer = lazy(
  () => import('./app/[brand]/administrators/creer/page'),
);
const AdministratorsImporter = lazy(
  () => import('./app/[brand]/administrators/importer/page'),
);
const AdministratorsModifier = lazy(
  () => import('./app/[brand]/administrators/modifier/page'),
);
const EmployeesPage = lazy(() => import('./app/[brand]/employees/page'));
const EmployeesCreer = lazy(() => import('./app/[brand]/employees/creer/page'));
const EmployeesImporter = lazy(
  () => import('./app/[brand]/employees/importer/page'),
);
const EmployeesModifier = lazy(
  () => import('./app/[brand]/employees/modifier/page'),
);
const Logout = lazy(() => import('./app/[brand]/logout/page'));
const Login = lazy(() => import('./app/[brand]/login/page'));
const Menu = lazy(() => import('./app/[brand]/menu/page'));
const NumeroAgrementPage = lazy(
  () => import('./app/[brand]/numero-agrement/page'),
);
const NumeroAgrementCreer = lazy(
  () => import('./app/[brand]/numero-agrement/creer/page'),
);
const NumeroAgrementImporter = lazy(
  () => import('./app/[brand]/numero-agrement/importer/page'),
);
const NumeroAgrementModifier = lazy(
  () => import('./app/[brand]/numero-agrement/modifier/page'),
);
const NumeroAgrementResultats = lazy(
  () => import('./app/[brand]/numero-agrement/resultats/page'),
);
const Recap = lazy(() => import('./app/[brand]/recap/page'));
const SocietePage = lazy(() => import('./app/[brand]/societe/page'));
const SocieteModifier = lazy(
  () => import('./app/[brand]/societe/modifier/page'),
);
const SocieteCreer = lazy(() => import('./app/[brand]/societe/creer/page'));
const ResetPassword = lazy(() => import('./app/[brand]/resetPwd/page'));

const RestrictedElement = ({
  children,
  status = 'LOGGED_IN',
  pageName = 'NoName',
}: {
  children: any;
  status?: 'LOGGED_IN' | 'LOGGED_OUT';
  pageName?: string;
}) => {
  return (
    <RestrictedPage status={status} pageName={pageName}>
      {children}
    </RestrictedPage>
  );
};

function App() {
  const { get: getBrand } = useBrand();
  const location = useLocation();

  useEffect(() => {
    getBrand();
  }, [, window.location, location]);

  return (
    <div className="App" id="main-app">
      <div className={`${styles.wrapper} ${getBrand() ?? Brand.CETELEM} gap-l`}>
        <Header brand={getBrand() ?? Brand.CETELEM} />
        <Routes>
          {Object.values(Brand).map((brand) => (
            <>
              <Route key={'init'} path={'/'} element={<Init />} />
              <Route key={'home'} path={brand} element={<Home />} />
              <Route
                key={'login'}
                path={brand + '/login'}
                element={<Login />}
              />
              <Route
                key={'administrators'}
                path={brand + '/administrators'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected="admin" />
                      <AdministratorsPage />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'creerAdministrators'}
                path={brand + '/administrators/creer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected="admin" />
                      <AdministratorsCreer />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'importerAdministrators'}
                path={brand + '/administrators/importer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected="admin" />
                      <AdministratorsImporter />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'modifierAdministrators'}
                path={brand + '/administrators/modifier'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected="admin" />
                      <AdministratorsModifier />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'employees'}
                path={brand + '/employees'}
                element={
                  <RestrictedElement pageName="employees">
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <EmployeesPage />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'creerEmployees'}
                path={brand + '/employees/creer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <EmployeesCreer />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'importerEmployees'}
                path={brand + '/employees/importer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <EmployeesImporter />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'modifierEmployees'}
                path={brand + '/employees/modifier'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <EmployeesModifier />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'logout'}
                path={brand + '/logout'}
                element={
                  <RestrictedElement status={'LOGGED_OUT'} pageName="Logout">
                    <Logout />
                  </RestrictedElement>
                }
              />
              <Route
                key={'menu'}
                path={brand + '/menu'}
                element={
                  <RestrictedElement>
                    <Menu />
                  </RestrictedElement>
                }
              />
              <Route
                key={'numeroAgrement'}
                path={brand + '/numero-agrement'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'pdv'} />
                      <NumeroAgrementPage />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'creeNumeroAgrement'}
                path={brand + '/numero-agrement/creer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'pdv'} />
                      <NumeroAgrementCreer />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'importerNumeroAgrement'}
                path={brand + '/numero-agrement/importer'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'pdv'} />
                      <NumeroAgrementImporter />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'modifierNumeroAgrement'}
                path={brand + '/numero-agrement/modifier'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'pdv'} />
                      <NumeroAgrementModifier />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'resultatsNumeroAgrement'}
                path={brand + '/numero-agrement/resultats'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'pdv'} />
                      <NumeroAgrementResultats />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'recap'}
                path={brand + '/recap'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <Recap />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'resetPassword'}
                path={brand + '/resetPassword'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected={'employee'} />
                      <ResetPassword />
                    </div>
                  </RestrictedElement>
                }
              />
              <Route
                key={'societe'}
                path={brand + '/societe'}
                element={
                  <RestrictedElement>
                    <SocietePage />
                  </RestrictedElement>
                }
              />
              <Route
                key={'creerSociete'}
                path={brand + '/societe/creer'}
                element={
                  <RestrictedElement>
                    <SocieteCreer />
                  </RestrictedElement>
                }
              />
              <Route
                key={'modifierSociete'}
                path={brand + '/societe/modifier'}
                element={
                  <RestrictedElement>
                    <div className={`flex-row`}>
                      <NavBar itemSelected="setting" />
                      <SocieteModifier />
                    </div>
                  </RestrictedElement>
                }
              />
            </>
          ))}
        </Routes>
      </div>
    </div>
  );
}

export default App;
